import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import {
  TmsProviderProps,
  TmsContextType,
} from '@awaze/mfe-common.contexts.tms'
import { TmsEventType } from '@awaze/mfe-common.contexts.tms/dist/DataLayer/DataLayer.types'
import { Store } from '../../store/type'

type PageLoadTms = TmsProviderProps['pageLoadDataObject']
type PageLoadInitialProps = Pick<
  PageLoadTms,
  'page_name' | 'page_section' | 'page_type'
>
type Params = Pick<PageLoadTms, 'page_name' | 'page_section' | 'page_type'> & {
  brand: Brand
  environment: Store['environment']
  locale: string
}

const getGAEnvironment = (environment: Store['environment']) => {
  switch (environment) {
    case 'pre-prod':
      return 'pprd'
    case 'prod':
      return 'prd'
    default:
      return environment
  }
}

export const TMSDefaultPageProps = ({
  brand,
  environment,
  locale,
  ...params
}: Params): PageLoadTms & { is_spa: string } => ({
  ...params,
  is_spa: '1',
  site_environment: getGAEnvironment(environment),
  site_version: process.env.NEXT_PUBLIC_GIT_HASH ?? 'not set',
  site_currency: 'GBP', // todo
  site_brand: brand,
  site_sub_brand: '',
  site_locale: locale.toLowerCase(),
  awaze_device_type: 'web',
})

export function updateDataLayer<EventData extends Record<string, unknown>>(
  tms: TmsContextType,
  eventType: TmsEventType,
  eventName: string,
  eventData?: EventData,
): void {
  tms?.updateDataLayer(eventType, eventName, eventData ?? {})
}

export async function hash(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message) // encode as (utf-8) Uint8Array
  const hashBuffer = await crypto?.subtle?.digest('SHA-256', msgUint8) // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)) // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('') // convert bytes to hex string
  return hashHex
}

export function getInitialPageDataByPath(path: string): PageLoadInitialProps {
  // Remove query parameters and leading/trailing slashes
  const normalizedPath = path.split('?')[0].replace(/(^\/+)|(\/+$)/g, '')

  switch (normalizedPath) {
    case 'forgotten-password':
      return {
        page_type: 'credential recovery',
        page_section: 'authentication',
        page_name: 'forgot password',
      }
    case 'forgotten-password/reset-password':
      return {
        page_type: 'credential recovery',
        page_section: 'authentication',
        page_name: 'reset password',
      }
    case 'forgotten-password/confirmation':
      return {
        page_type: 'credential recovery',
        page_section: 'authentication',
        page_name: 'forgotten password confirm',
      }
    case 'login':
      return {
        page_type: 'log in',
        page_section: 'authentication',
        page_name: 'log in',
      }
    case 'logout':
      return {
        page_type: 'log out',
        page_section: 'authentication',
        page_name: 'authentication log out',
      }
    case 'sign-up':
      return {
        page_type: 'sign up',
        page_section: 'authentication',
        page_name: 'create account',
      }
    case 'sign-up/activation':
      return {
        page_type: 'sign up',
        page_section: 'authentication',
        page_name: 'account activation',
      }
    case 'sign-up/activation/expired':
      return {
        page_type: 'sign up',
        page_section: 'authentication',
        page_name: 'account activation expired',
      }
    case 'sign-up/confirmation':
      return {
        page_type: 'sign up',
        page_section: 'authentication',
        page_name: 'create account confirm',
      }
    default:
      return {
        page_type: normalizedPath,
        page_section: 'authentication',
        page_name: normalizedPath,
      }
  }
}
